import { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HubAccessContext = createContext({
  hubs: {},
  planName: null,
  planStatus: null,
  loading: true,
  hasAccess: () => false,
});

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export function HubAccessProvider({ children, user }) {
  const [hubData, setHubData] = useState({ hubs: {}, planName: null, planStatus: null });
  const [loading, setLoading] = useState(true);

  const fetchHubAccess = useCallback(async () => {
    if (!user) {
      setHubData({ hubs: {}, planName: null, planStatus: null });
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/tenant/hubs`, { headers: getHeaders() });
      setHubData({
        hubs: data.hubs || {},
        planName: data.plan_name || null,
        planStatus: data.plan_status || null,
      });
    } catch (err) {
      // Fallback: if API fails, don't restrict (default open)
      setHubData({ hubs: {}, planName: null, planStatus: null });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchHubAccess();
  }, [fetchHubAccess]);

  const hasAccess = useCallback((hubKey) => {
    if (!hubKey) return true;
    // If hubData is empty (no plan data), default to accessible
    if (Object.keys(hubData.hubs).length === 0) return true;
    return hubData.hubs[hubKey] !== false;
  }, [hubData]);

  return (
    <HubAccessContext.Provider value={{ ...hubData, loading, hasAccess }}>
      {children}
    </HubAccessContext.Provider>
  );
}

export function useHubAccess() {
  return useContext(HubAccessContext);
}

export default HubAccessContext;
